import { Component, Suspense, createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { App, Button, ConfigProvider, Dropdown, Form, Result, Spin } from 'antd';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, RefreshCw, X } from 'lucide-react';
import { createPath, Routes, UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom';
import { canonicalHref, clearWorkspaceStorage, restoreWorkspace, routeKey, safeHref, safeViewState, WORKSPACE_STORAGE } from './workspaceState';
import type { ViewState, WorkspaceRoute, WorkspaceTab } from './workspaceState';
import { useWorkspaceOperationPending, workspaceOperationPending } from './workspaceBusy';
import './admin-workspace.css';
import { WorkspaceVisibility } from './workspaceQuery';

type Workspace = {
  tabs: WorkspaceTab[]; active: string; routes: WorkspaceRoute[];
  visit: (href: string) => void; update: (key: string, href: string, replace: boolean) => void;
  close: (keys: string[]) => Promise<void>; mayLeave: (keys: string[]) => Promise<boolean>;
  panels: React.RefObject<Map<string, HTMLElement>>;
  views: React.RefObject<Record<string, ViewState>>; persist: () => void; busy: boolean;
};
const Context = createContext<Workspace | null>(null);
const ViewContext = createContext<{ state: ViewState; persist: () => void } | null>(null);
export const useAdminWorkspace = () => useContext(Context)!;

/** Only explicit list-view state belongs here; editing state stays in the retained page. */
export function useWorkspaceState<T,>(key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const view = useContext(ViewContext);
  const [value, setValue] = useState<T>(() => {
    const base = typeof initial === 'function' ? (initial as () => T)() : initial;
    const cached = view?.state[key];
    if (cached === undefined) return base;
    return base && typeof base === 'object' && !Array.isArray(base) ? { ...base, ...cached as object } : cached as T;
  });
  useEffect(() => { if (view) { view.state[key] = value; view.persist(); } }, [key, value, view]);
  return [value, setValue];
}

export function AdminWorkspaceProvider({ owner, routes, children }: { owner: string; routes: WorkspaceRoute[]; children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { message, modal } = App.useApp();
  const confirming = useRef(false);
  const cancelConfirmation = useRef<(() => void) | null>(null);
  // Session expiry unmounts this provider: do not leave a stale account's dialog open.
  useEffect(() => () => cancelConfirmation.current?.(), []);
  const [restored] = useState(() => {
    let raw = null;
    try { raw = sessionStorage.getItem(WORKSPACE_STORAGE); } catch { /* Use memory only. */ }
    return restoreWorkspace(raw, owner, routes, `${location.pathname}${location.search}`);
  });
  const [tabs, setTabs] = useState(restored.tabs);
  const tabsRef = useRef(tabs); tabsRef.current = tabs;
  const views = useRef(restored.views);
  const panels = useRef(new Map<string, HTMLElement>());
  const href = canonicalHref(`${location.pathname}${location.search}`);
  const active = routeKey(href, routes);
  const activeRef = useRef(active); activeRef.current = active;
  const mutating = useIsMutating();
  const pending = useWorkspaceOperationPending();
  const busy = mutating > 0 || pending;
  const persist = () => {
    try {
      sessionStorage.setItem(WORKSPACE_STORAGE, JSON.stringify({ version: 1, owner,
        tabs: tabsRef.current.map(t => ({ key: t.key, href: safeHref(t.href) })),
        views: Object.fromEntries(tabsRef.current.map(t => [t.key, safeViewState(views.current[t.key] || {})]))
      }));
    } catch { /* Quota/private mode must not break navigation. */ }
  };
  useLayoutEffect(() => {
    if (`${location.pathname}${location.search}` !== href) { navigate(href, { replace: true }); return; }
    setTabs(current => {
      const existing = current.find(t => t.key === active);
      if (existing?.href === href && existing.visited) return current;
      return existing ? current.map(t => t.key === active ? { ...t, href, visited: true } : t)
        : [...current, { key: active, href, visited: true }];
    });
  }, [href, active, location.pathname, location.search, navigate]);
  useEffect(persist, [tabs]);
  // A successful write can affect another function. Invalidate quietly; visible observers
  // retain their page-specific refresh logic, hidden observers refetch on reactivation.
  useEffect(() => client.getMutationCache().subscribe(event => {
    if (event.type === 'updated' && event.action.type === 'success') void client.invalidateQueries({ refetchType: 'none' });
  }), [client]);
  const hasDraft = (keys: string[]) => keys.some(key => {
    const panel = panels.current.get(key);
    if (!panel) return false;
    if (panel.querySelector('.ant-modal-wrap:not([style*="display: none"]), .ant-drawer-open')) return true;
    return Array.from(panel.querySelectorAll('[data-workspace-dirty="true"]')).some(form =>
      !form.closest('.ant-modal-wrap[style*="display: none"], .ant-drawer:not(.ant-drawer-open)'));
  });
  const mayLeave = async (keys: string[]) => {
    if (client.isMutating() || workspaceOperationPending()) { message.warning('操作或下载正在处理中，请完成后再关闭或退出。'); return false; }
    if (!hasDraft(keys)) return true;
    if (confirming.current) return false;
    confirming.current = true;
    try {
      const accepted = await new Promise<boolean>(resolve => {
        const dialog = modal.confirm({
        title: '关闭页面前请确认',
        content: `即将关闭：${keys.map(key => routes.find(r => r.key === key)?.label).filter(Boolean).join('、')}。编辑内容和打开的详情将不再保留。`,
        okText: '确认关闭', cancelText: '继续编辑',
        onOk: () => resolve(true), onCancel: () => resolve(false)
        });
        cancelConfirmation.current = () => { dialog.destroy(); resolve(false); };
      });
      if (accepted && (client.isMutating() || workspaceOperationPending())) {
        message.warning('操作正在处理中，请稍后再关闭或退出。');
        return false;
      }
      return accepted;
    } finally { confirming.current = false; cancelConfirmation.current = null; }
  };
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (client.isMutating() || workspaceOperationPending() || hasDraft(tabsRef.current.map(t => t.key))) {
        event.preventDefault(); event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [client]);
  const visit = (target: string) => {
    const key = routeKey(target, routes);
    const existing = tabsRef.current.find(t => t.key === key);
    navigate(existing?.href || target);
  };
  const update = (key: string, target: string, replace: boolean) => {
    if (activeRef.current === key) { navigate(target, { replace }); return; }
    // Late work from an inactive page must never steal focus or open another function.
    if (routeKey(target, routes) === key) setTabs(current => current.map(t => t.key === key ? { ...t, href: target } : t));
  };
  const close = async (keys: string[]) => {
    const closing = keys.filter(key => key !== '/');
    if (!closing.length || !await mayLeave(closing)) return;
    // Recheck after the asynchronous confirmation: a write may have started meanwhile.
    if (client.isMutating() || workspaceOperationPending()) { message.warning('操作正在处理中，请稍后再关闭。'); return; }
    const remaining = tabsRef.current.filter(t => !closing.includes(t.key));
    closing.forEach(key => { delete views.current[key]; });
    if (closing.includes(activeRef.current)) {
      const index = tabsRef.current.findIndex(t => t.key === activeRef.current);
      const next = tabsRef.current.slice(0, index).reverse().find(t => !closing.includes(t.key)) || remaining[0];
      navigate(next.href, { replace: true });
    }
    setTabs(remaining);
  };
  return <Context.Provider value={{ tabs, active, routes, visit, update, close, mayLeave, panels, views, persist, busy }}>{children}</Context.Provider>;
}

export function AdminWorkspace({ children }: { children: ReactNode }) {
  const workspace = useAdminWorkspace();
  const client = useQueryClient();
  const { message } = App.useApp();
  const [refreshing, setRefreshing] = useState(false);
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    strip.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [workspace.active]);
  const refresh = async () => {
    if (refreshing || client.isMutating() || workspaceOperationPending()) return;
    setRefreshing(true);
    try {
      await client.refetchQueries({ type: 'active' }, { throwOnError: true });
      message.success('当前页面数据已刷新，筛选与编辑内容已保留');
    } catch { message.error('数据刷新失败，请检查页面提示后重试'); }
    finally { setRefreshing(false); }
  };
  return <div className="admin-workspace">
    <div className="admin-workspace-bar">
      <div ref={strip} className="admin-workspace-tablist" role="tablist" aria-label="已打开的功能" onKeyDown={event => {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const index = workspace.tabs.findIndex(t => t.key === workspace.active);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? workspace.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + workspace.tabs.length) % workspace.tabs.length;
        workspace.visit(workspace.tabs[next].href);
        strip.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
      }}>
        {workspace.tabs.map(tab => <div key={tab.key} className={`admin-workspace-tab ${tab.key === workspace.active ? 'is-active' : ''}`}>
          <button role="tab" id={`workspace-tab-${tab.key}`} aria-controls={`workspace-panel-${tab.key}`} aria-selected={tab.key === workspace.active} tabIndex={tab.key === workspace.active ? 0 : -1} onClick={() => workspace.visit(tab.href)}>{workspace.routes.find(r => r.key === tab.key)?.label || '平台概览'}</button>
          {tab.key !== '/' && <button className="workspace-close" aria-label={`关闭${workspace.routes.find(r => r.key === tab.key)?.label}`} disabled={workspace.busy} onClick={() => workspace.close([tab.key])}><X size={14} /></button>}
        </div>)}
      </div>
      <div className="admin-workspace-tools">
        <Button type="text" icon={<RefreshCw size={15}/>} onClick={refresh} loading={refreshing} disabled={workspace.busy}>刷新数据</Button>
        <Dropdown trigger={['click']} menu={{ items: [
          ...workspace.tabs.map(t => ({ key: t.key, label: workspace.routes.find(r => r.key === t.key)?.label, onClick: () => workspace.visit(t.href) })),
          { type: 'divider' },
          { key: 'close-others', label: '关闭其他标签', disabled: workspace.busy || workspace.tabs.length <= 1, onClick: () => workspace.close(workspace.tabs.filter(t => t.key !== workspace.active).map(t => t.key)) },
          { key: 'close-all', label: '关闭全部标签', disabled: workspace.busy || workspace.tabs.length <= 1, onClick: () => workspace.close(workspace.tabs.map(t => t.key)) }
        ] }}><Button type="text" aria-label="标签管理" icon={<ChevronDown size={16}/>}/></Dropdown>
      </div>
    </div>
    {workspace.tabs.map(tab => <WorkspacePanel key={tab.key} tab={tab}>{children}</WorkspacePanel>)}
  </div>;
}

function WorkspacePanel({ tab, children }: { tab: WorkspaceTab; children: ReactNode }) {
  const workspace = useAdminWorkspace();
  const parentNavigation = useContext(UNSAFE_NavigationContext);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const active = tab.key === workspace.active;
  const latest = useRef(workspace); latest.current = workspace;
  const navigation = useMemo(() => ({ ...parentNavigation, navigator: {
    ...parentNavigation.navigator,
    push: (to: Parameters<typeof createPath>[0] | string) => latest.current.update(tab.key, typeof to === 'string' ? to : createPath(to), false),
    replace: (to: Parameters<typeof createPath>[0] | string) => latest.current.update(tab.key, typeof to === 'string' ? to : createPath(to), true),
    go: (delta: number) => { if (latest.current.active === tab.key) parentNavigation.navigator.go(delta); }
  } }), [parentNavigation, tab.key]);
  const view = useMemo(() => ({ state: workspace.views.current[tab.key] ||= {}, persist: () => latest.current.persist() }), [tab.key, workspace.views]);
  const markForms = () => {
    host?.parentElement?.querySelectorAll('form').forEach(form => { form.dataset.workspaceDirty = 'true'; });
  };
  return <section ref={node => { if (node) workspace.panels.current.set(tab.key, node); else workspace.panels.current.delete(tab.key); }}
    className="admin-workspace-panel" id={`workspace-panel-${tab.key}`} role="tabpanel" aria-labelledby={`workspace-tab-${tab.key}`} hidden={!active}
    onChangeCapture={event => { const form = (event.target as HTMLElement).closest('form'); if (form) form.dataset.workspaceDirty = 'true'; }}>
    <div className="workspace-popup-host" ref={setHost}/>
    {host && tab.visited && <WorkspaceVisibility.Provider value={active}>
      {/* Retained hidden dialogs must release their document-level focus locks. */}
      <ConfigProvider getPopupContainer={() => host} modal={{ focusable: { trap: active } }} drawer={{ focusable: { trap: active } }}>
        <App message={{ getContainer: () => host }} notification={{ getContainer: () => host }}>
          <Form.Provider onFormChange={markForms}>
            <ViewContext.Provider value={view}>
              <UNSAFE_NavigationContext.Provider value={navigation}>
                <div className="admin-content workspace-page-scroll"><PanelErrorBoundary><Suspense fallback={<div className="route-loader"><Spin size="large"/></div>}><Routes location={tab.href}>{children}</Routes></Suspense></PanelErrorBoundary></div>
              </UNSAFE_NavigationContext.Provider>
            </ViewContext.Provider>
          </Form.Provider>
        </App>
      </ConfigProvider>
    </WorkspaceVisibility.Provider>}
  </section>;
}

export { clearWorkspaceStorage };

class PanelErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <Result status="error" title="当前功能暂时无法显示" subTitle="其他标签仍可使用。请重新打开此功能；尚未提交的编辑需要重新填写。" extra={<Button onClick={() => this.setState({ failed: false })}>重新打开功能</Button>}/> : this.props.children;
  }
}
